import { useCallback, useEffect, useState } from 'react';

interface AsyncState<T> {
  readonly data: T | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly reload: () => void;
}

export function useAsyncData<T>(
  loader: () => Promise<T>,
  deps: readonly unknown[],
  errorMessage = 'Could not load data.',
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loader()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : errorMessage);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [...deps, reloadKey]);

  return { data, error, loading, reload };
}
